import React, { useState } from "react";
import { Form, Input, Button, Card, message, Alert } from "antd";
import { UserOutlined, LockOutlined, KeyOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { loginUser } from "../store/slices/authSlice";
import { authApi } from "../services/api";
import { AppDispatch, RootState } from "../store";
import { LoginCredentials } from "../types/auth.types";

const LoginPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { error, isAuthenticated } = useSelector(
    (state: RootState) => state.auth
  );
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();
  const [localRequiresOtp, setLocalRequiresOtp] = React.useState(false);
  const [initialCredentials, setInitialCredentials] =
    React.useState<LoginCredentials | null>(null);
  const [loginError, setLoginError] = React.useState<string | null>(null);
  const [isIpBlocked, setIsIpBlocked] = React.useState(false);
  const [isAccountLocked, setIsAccountLocked] = React.useState(false);

  const from = location.state?.from?.pathname || "/dashboard";

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  React.useEffect(() => {
    if (error) {
      setLoginError(error);
    }
  }, [error]);

  const onFinish = async (values: LoginCredentials) => {
    if (isLoading) return; // Prevent multiple submissions
    setIsLoading(true);
    setLoginError(null);

    try {
      if (!localRequiresOtp) {
        // First login attempt
        const response = await authApi.login(values);
        if (response.status === "OTP_REQUIRED") {
          setLocalRequiresOtp(true);
          setInitialCredentials(values);
          message.info(response.message || "Verification code sent to your email");
        } else if (response.status === "SUCCESS") {
          await dispatch(loginUser(response.data)).unwrap();
          message.success(response.message || "Login successful");
        } else {
          setLoginError(response.message || "Login failed. Please try again.");
        }
      } else {
        // OTP verification
        if (!initialCredentials) {
          setLoginError("Session expired. Please login again.");
          setLocalRequiresOtp(false);
          return;
        }
        const loginData = {
          ...initialCredentials,
          otp: values.otp,
        };
        const response = await authApi.login(loginData);
        if (response.status === "SUCCESS") {
          await dispatch(loginUser(response.data)).unwrap();
          message.success(response.message || "Login successful");
        } else {
          setLoginError(response.message || "Login failed. Please try again.");
        }
      }
    } catch (err: any) {
      // Handle axios errors
      console.log("err", err.response);
      setLoginError(err.response?.data?.message || err.message || "Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        padding: "40px 20px",
      }}
    >
      <Card
        title={localRequiresOtp ? "Enter Verification Code" : "Login"}
        style={{
          maxWidth: 400,
          width: "100%",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}
      >
        {loginError && (
          <Alert
            message="Login Error"
            description={<div>{loginError}</div>}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        
        <Form
          form={form}
          name="login"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
        >
          <Form.Item
            name="username"
            rules={[
              {
                required: !localRequiresOtp,
                message: "Please input your username!",
              },
            ]}
            hidden={localRequiresOtp}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Username"
              size="large"
              disabled={isLoading}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              {
                required: !localRequiresOtp,
                message: "Please input your password!",
              },
            ]}
            hidden={localRequiresOtp}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Password"
              size="large"
              disabled={isLoading}
            />
          </Form.Item>
          <p hidden={!localRequiresOtp}>
            A verification code has been sent to your email. Please enter it
            below to continue.
          </p>
          <Form.Item
            name="otp"
            rules={[
              {
                required: localRequiresOtp,
                message: "Please input the verification code!",
              },
            ]}
            hidden={!localRequiresOtp}
          >
            <Input
              prefix={<KeyOutlined />}
              placeholder="Verification Code"
              size="large"
              disabled={isLoading}
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={isLoading}
              disabled={isIpBlocked}
            >
              {localRequiresOtp ? "Verify" : "Log in"}
            </Button>
          </Form.Item>

          {localRequiresOtp && (
              <Form.Item>
                <Button
                    type="link"
                    block
                    onClick={async () => {
                      if (!initialCredentials) {
                        setLoginError("Session expired. Please login again.");
                        setLocalRequiresOtp(false);
                        return;
                      }
                      try {
                        setIsLoading(true);
                        const response = await authApi.login(initialCredentials);
                        if (response.status === "OTP_REQUIRED") {
                          message.info(response.message || "A new verification code has been sent to your email.");
                        } else {
                          setLoginError(response.message || "Failed to resend code.");
                        }
                      } catch (err: any) {
                        setLoginError(err.response?.data?.message || "Error resending verification code: " + (err.message || "Unknown error"));
                      } finally {
                        setIsLoading(false);
                      }
                    }}
                    disabled={isLoading}
                >
                  Resend Code
                </Button>
              </Form.Item>
          )}

          {!localRequiresOtp && (
            <Form.Item>
              <Button
                type="link"
                onClick={() => navigate("/forgot-password")}
                block
              >
                Forgot Password?
              </Button>
            </Form.Item>
          )}
        </Form>
      </Card>
    </div>
  );
};

export default LoginPage;
